import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection, query, where, onSnapshot, doc, Timestamp,
} from 'firebase/firestore';
import { setDoc, updateDoc, deleteDoc } from '../config/escrituras';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { usePerfil } from './PerfilContext';
import { Cliente } from '../types';

/**
 * Datos del módulo Clientes. Es transversal: hoy lo consume Taller 3D para
 * asignar un cliente a cada presupuesto, y mañana lo usarán Pedidos y Facturas.
 */
interface ClientesContextType {
  clientes: Cliente[];
  /** Solo los activos, ordenados por nombre. Es lo que va en los desplegables. */
  clientesActivos: Cliente[];
  loadingClientes: boolean;

  crearCliente: (c: Omit<Cliente, 'id' | 'usuarioId' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  actualizarCliente: (id: string, datos: Partial<Cliente>) => Promise<void>;
  eliminarCliente: (id: string) => Promise<void>;
  clientePorId: (id?: string) => Cliente | undefined;
}

const ClientesContext = createContext<ClientesContextType | undefined>(undefined);

const sinUndefined = (o: Record<string, any>) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));

export const ClientesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Solo se escucha con el módulo activado: sin él, las reglas rechazan la
  // consulta y el listener muere; al activarlo, el efecto vuelve a suscribirse.
  const { tieneModulo } = usePerfil();
  const conModulo = tieneModulo('clientes');
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(true);

  useEffect(() => {
    if (!user || !conModulo) {
      setClientes([]);
      setLoadingClientes(false);
      return;
    }

    setLoadingClientes(true);
    const q = query(collection(db, 'clientes'), where('usuarioId', '==', user.uid));

    const unsub = onSnapshot(q, snap => {
      const lista = snap.docs.map(d => ({
        ...d.data(),
        id: d.id,
        activo: d.data().activo !== false,
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate() || d.data().createdAt?.toDate() || new Date(),
      })) as Cliente[];
      lista.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
      setClientes(lista);
      setLoadingClientes(false);
    });

    return unsub;
  }, [user, conModulo]);

  const clientesActivos = useMemo(() => clientes.filter(c => c.activo), [clientes]);

  const crearCliente: ClientesContextType['crearCliente'] = async c => {
    if (!user) throw new Error('Sesión no iniciada');
    const ref = doc(collection(db, 'clientes'));
    await setDoc(ref, sinUndefined({
      ...c,
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }));
    return ref.id;
  };

  const actualizarCliente = async (id: string, datos: Partial<Cliente>) => {
    await updateDoc(doc(db, 'clientes', id), sinUndefined({
      ...datos, updatedAt: Timestamp.now(),
    }));
  };

  const eliminarCliente = async (id: string) => {
    await deleteDoc(doc(db, 'clientes', id));
  };

  const clientePorId = (id?: string) => (id ? clientes.find(c => c.id === id) : undefined);

  return (
    <ClientesContext.Provider value={{
      clientes, clientesActivos, loadingClientes,
      crearCliente, actualizarCliente, eliminarCliente, clientePorId,
    }}>
      {children}
    </ClientesContext.Provider>
  );
};

export const useClientes = () => {
  const ctx = useContext(ClientesContext);
  if (!ctx) throw new Error('useClientes debe usarse dentro de ClientesProvider');
  return ctx;
};
